import { Badge } from '@/components/ui/common/Badge'

;('use client')

import { Input } from '@/components/forms/inputs/text/Input'
import { Button } from '@/components/ui/common/Button'
import { cn } from '@/utils'
import DashboardLayout from '@/views/dashboard/DashboardLayout'
import {
  Brain,
  CheckCircle,
  ChevronRight,
  Clock,
  FileText,
  Info,
  Play,
  Search,
  Target,
  Users
} from 'lucide-react'
import { useState } from 'react'

interface NeuropsychTest {
  id: number
  name: string
  category: string
  description: string
  duration: string
  ageRange: string
  difficulty: 'Łatwy' | 'Średni' | 'Trudny'
  domains: string[]
  reliability: number
  validity: number
  normativeData: boolean
  language: string[]
  administration: 'Indywidualna' | 'Grupowa' | 'Komputerowa'
  materials: string[]
  interpretation: string
  applications: string[]
  icon: any
  color: string
}

function NeurophysicsTests() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('Wszystkie')
  const [selectedTest, setSelectedTest] = useState<NeuropsychTest | null>(null)

  const tests: NeuropsychTest[] = [
    {
      id: 1,
      name: 'Test Stroopa',
      category: 'Funkcje wykonawcze',
      description:
        'Klasyczny test oceniający kontrolę uwagi, hamowanie reakcji automatycznych i elastyczność poznawczą.',
      duration: '10-15 minut',
      ageRange: '6-90 lat',
      difficulty: 'Średni',
      domains: ['Uwaga', 'Funkcje wykonawcze', 'Kontrola poznawcza'],
      reliability: 0.85,
      validity: 0.82,
      normativeData: true,
      language: ['Polski', 'Angielski'],
      administration: 'Indywidualna',
      materials: ['Karty ze słowami', 'Karty kolorowe', 'Instrukcja'],
      interpretation:
        'Czas reakcji i liczba błędów wskazują na sprawność funkcji wykonawczych i kontroli uwagi.',
      applications: ['ADHD', 'Demencja', 'Urazy mózgu', 'Schizofrenia'],
      icon: Brain,
      color: '#2db2b8'
    },
    {
      id: 2,
      name: 'Test Figury Złożonej Rey-Osterrieth',
      category: 'Pamięć wzrokowa',
      description:
        'Test oceniający pamięć wzrokową, funkcje wzrokowo-przestrzenne oraz planowanie i organizację.',
      duration: '30-45 minut',
      ageRange: '6-89 lat',
      difficulty: 'Trudny',
      domains: ['Pamięć wzrokowa', 'Funkcje wzrokowo-przestrzenne', 'Planowanie'],
      reliability: 0.88,
      validity: 0.79,
      normativeData: true,
      language: ['Polski', 'Angielski', 'Francuski'],
      administration: 'Indywidualna',
      materials: ['Karta z figurą', 'Papier', 'Kolorowe ołówki'],
      interpretation:
        'Ocena dokładności kopiowania i odtwarzania z pamięci złożonej figury geometrycznej.',
      applications: ['Demencja', 'Urazy mózgu', 'Zaburzenia uczenia się'],
      icon: Target,
      color: '#2db2b8'
    },
    {
      id: 3,
      name: 'Wisconsin Card Sorting Test (WCST)',
      category: 'Funkcje wykonawcze',
      description:
        'Test oceniający elastyczność poznawczą, formowanie pojęć abstrakcyjnych i zdolność do zmiany strategii.',
      duration: '20-30 minut',
      ageRange: '6.5-89 lat',
      difficulty: 'Trudny',
      domains: ['Elastyczność poznawcza', 'Formowanie pojęć', 'Funkcje wykonawcze'],
      reliability: 0.83,
      validity: 0.85,
      normativeData: true,
      language: ['Polski', 'Angielski'],
      administration: 'Komputerowa',
      materials: ['Komputer', 'Oprogramowanie WCST', 'Instrukcja'],
      interpretation:
        'Analiza błędów perseweracyjnych i zdolności do uczenia się nowych reguł.',
      applications: ['Schizofrenia', 'Autyzm', 'Urazy płata czołowego'],
      icon: CheckCircle,
      color: '#2db2b8'
    },
    {
      id: 4,
      name: 'Trail Making Test (TMT)',
      category: 'Uwaga',
      description:
        'Test oceniający uwagę podzielną, szybkość przetwarzania i elastyczność poznawczą.',
      duration: '5-10 minut',
      ageRange: '8-89 lat',
      difficulty: 'Łatwy',
      domains: ['Uwaga podzielna', 'Szybkość przetwarzania', 'Funkcje wykonawcze'],
      reliability: 0.79,
      validity: 0.77,
      normativeData: true,
      language: ['Polski', 'Angielski'],
      administration: 'Indywidualna',
      materials: ['Arkusze TMT A i B', 'Stoper', 'Ołówek'],
      interpretation:
        'Czas wykonania i liczba błędów wskazują na sprawność uwagi i funkcji wykonawczych.',
      applications: ['Demencja', 'ADHD', 'Urazy mózgu'],
      icon: Clock,
      color: '#2db2b8'
    },
    {
      id: 5,
      name: 'Test Pamięci Słuchowej Cyfrowej',
      category: 'Pamięć robocza',
      description:
        'Test oceniający pojemność pamięci roboczej słuchowej i zdolność manipulowania informacjami.',
      duration: '10-15 minut',
      ageRange: '6-90 lat',
      difficulty: 'Średni',
      domains: ['Pamięć robocza', 'Uwaga słuchowa', 'Przetwarzanie sekwencyjne'],
      reliability: 0.87,
      validity: 0.81,
      normativeData: true,
      language: ['Polski', 'Angielski'],
      administration: 'Indywidualna',
      materials: ['Protokół testowy', 'Instrukcja'],
      interpretation:
        'Rozpiętość cyfrowa wprost i wstecz wskazuje na sprawność pamięci roboczej.',
      applications: ['Zaburzenia uczenia się', 'ADHD', 'Demencja'],
      icon: Users,
      color: '#2db2b8'
    },
    {
      id: 6,
      name: 'Test Fluencji Słownej',
      category: 'Funkcje językowe',
      description:
        'Test oceniający fluencję słowną, dostęp do słownika mentalnego i funkcje wykonawcze.',
      duration: '5-10 minut',
      ageRange: '6-90 lat',
      difficulty: 'Łatwy',
      domains: ['Fluencja słowna', 'Funkcje językowe', 'Funkcje wykonawcze'],
      reliability: 0.82,
      validity: 0.78,
      normativeData: true,
      language: ['Polski', 'Angielski'],
      administration: 'Indywidualna',
      materials: ['Protokół testowy', 'Stoper'],
      interpretation: 'Liczba słów w kategoriach semantycznych i fonemicznych.',
      applications: ['Afazja', 'Demencja', 'Urazy płata czołowego'],
      icon: FileText,
      color: '#2db2b8'
    }
  ]

  const categories = [
    'Wszystkie',
    'Funkcje wykonawcze',
    'Pamięć wzrokowa',
    'Uwaga',
    'Pamięć robocza',
    'Funkcje językowe'
  ]

  const filteredTests = tests.filter((test) => {
    const matchesSearch =
      test.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      test.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory =
      selectedCategory === 'Wszystkie' || test.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'Łatwy':
        return 'text-green-600 bg-green-50 border-green-200'
      case 'Średni':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      case 'Trudny':
        return 'text-red-600 bg-red-50 border-red-200'
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  const getAdministrationColor = (type: string) => {
    switch (type) {
      case 'Indywidualna':
        return 'text-[#2bcaca] bg-[#2bcaca]/10 border-[#2bcaca]/20'
      case 'Grupowa':
        return 'text-[#2bcaca] bg-[#2bcaca]/10 border-[#2bcaca]/20'
      case 'Komputerowa':
        return 'text-[#2bcaca] bg-[#2bcaca]/10 border-[#2bcaca]/20'
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  if (selectedTest) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <div className="mb-6">
            <button
              className="mb-4 flex items-center text-[#2bcaca] hover:text-[#229999]"
              onClick={() => setSelectedTest(null)}
            >
              <ChevronRight className="mr-1 h-4 w-4 rotate-180 transform" />
              Powrót do listy testów
            </button>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <selectedTest.icon className="mr-3 h-8 w-8 text-[#2bcaca]" />
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    {selectedTest.name}
                  </h1>
                  <p className="mt-1 text-gray-600">{selectedTest.category}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-[#2bcaca]">
                  {selectedTest.reliability * 100}%
                </div>
                <p className="text-sm text-gray-500">rzetelność</p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Left Column */}
            <div className="space-y-6 lg:col-span-2">
              {/* Description */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Opis testu</h2>
                <p className="leading-relaxed text-gray-700">
                  {selectedTest.description}
                </p>
              </div>

              {/* Domains */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Oceniane obszary</h2>
                <div className="flex flex-wrap gap-2">
                  {selectedTest.domains.map((domain, index) => (
                    <Badge
                      className="border-[#2bcaca]/20 bg-[#2bcaca]/10 text-[#2bcaca] rounded-sm"
                      key={index}
                      variant="outline"
                    >
                      {domain}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Materials */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Materiały</h2>
                <ul className="space-y-2">
                  {selectedTest.materials.map((material, index) => (
                    <li className="flex items-start" key={index}>
                      <Info className="mt-1 mr-2 h-4 w-4 flex-shrink-0 text-[#2bcaca]" />
                      <span className="text-gray-700">{material}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Interpretation */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Interpretacja</h2>
                <p className="text-gray-700">{selectedTest.interpretation}</p>
              </div>

              {/* Applications */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Zastosowania</h2>
                <div className="flex flex-wrap gap-2">
                  {selectedTest.applications.map((app, index) => (
                    <Badge
                      className="border-[#2bcaca]/20 bg-[#2bcaca]/10 text-[#2bcaca] rounded-sm"
                      key={index}
                      variant="outline"
                    >
                      {app}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Kluczowe informacje</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Clock className="mr-2 h-4 w-4 text-[#2bcaca]" />
                      <span className="text-sm text-gray-600">Czas trwania</span>
                    </div>
                    <span className="font-medium">{selectedTest.duration}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Users className="mr-2 h-4 w-4 text-[#2bcaca]" />
                      <span className="text-sm text-gray-600">Zakres wiekowy</span>
                    </div>
                    <span className="font-medium">{selectedTest.ageRange}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Target className="mr-2 h-4 w-4 text-[#2bcaca]" />
                      <span className="text-sm text-gray-600">Trudność</span>
                    </div>
                    <Badge
                      className={getDifficultyColor(selectedTest.difficulty)}
                      variant="outline"
                    >
                      {selectedTest.difficulty}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FileText className="mr-2 h-4 w-4 text-[#2bcaca]" />
                      <span className="text-sm text-gray-600">Administracja</span>
                    </div>
                    <Badge
                      className={getAdministrationColor(selectedTest.administration)}
                      variant="outline"
                    >
                      {selectedTest.administration}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Languages */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Dostępne wersje językowe</h2>
                <div className="flex flex-wrap gap-2">
                  {selectedTest.language.map((lang, index) => (
                    <Badge
                      className="border-[#2bcaca]/20 bg-[#2bcaca]/10 text-[#2bcaca] rounded-sm"
                      key={index}
                      variant="outline"
                    >
                      {lang}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Działania</h2>
                <div className="space-y-3">
                  <Button className="w-full bg-[#2bcaca] text-white hover:bg-[#229999] rounded-sm">
                    <Play className="mr-2 h-4 w-4" />
                    Rozpocznij test
                  </Button>
                  <Button className="w-full rounded-sm" variant="outline">
                    <FileText className="mr-2 h-4 w-4" />
                    Pobierz protokół
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">Testy neuropsychologiczne</h1>
          <p className="text-gray-600">
            Specjalistyczne narzędzia do oceny funkcji poznawczych
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                <Input
                  className="pl-10 border-gray-200"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Szukaj testu..."
                  value={searchTerm}
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  className={cn(
                    selectedCategory === category
                      ? 'bg-[#2bcaca] text-white hover:bg-[#229999]'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  size="sm"
                  variant={selectedCategory === category ? 'default' : 'outline'}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredTests.map((test) => (
            <div
              className="cursor-pointer border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md flex flex-col h-full"
              key={test.id}
              onClick={() => setSelectedTest(test)}
            >
              {/* Header */}
              <div className="mb-4 flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="mb-1 font-semibold text-gray-900">{test.name}</h3>
                  <p className="text-sm text-gray-500">{test.category}</p>
                </div>
                <div className="ml-4 text-right">
                  <div className="text-lg font-bold text-[#2bcaca]">
                    {test.reliability * 100}%
                  </div>
                  <p className="text-xs text-gray-500">rzetelność</p>
                </div>
              </div>

              {/* Description */}
              <p className="mb-4 line-clamp-3 text-sm text-gray-700 flex-grow">
                {test.description}
              </p>

              {/* Stats */}
              <div className="mb-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Czas trwania:</span>
                  <span className="font-medium">{test.duration}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Trudność:</span>
                  <Badge
                    className={getDifficultyColor(test.difficulty)}
                    size="sm"
                    variant="outline"
                  >
                    {test.difficulty}
                  </Badge>
                </div>
              </div>

              {/* Domains Preview */}
              <div className="mb-4">
                <p className="mb-2 text-xs text-gray-500">Oceniane obszary:</p>
                <div className="flex flex-wrap gap-1">
                  {test.domains.slice(0, 3).map((domain, index) => (
                    <Badge
                      className="border-[#2bcaca]/20 bg-[#2bcaca]/10 text-[#2bcaca] rounded-sm text-xs"
                      key={index}
                      size="sm"
                      variant="outline"
                    >
                      {domain}
                    </Badge>
                  ))}
                  {test.domains.length > 3 && (
                    <Badge
                      className="border-[#2bcaca]/20 bg-[#2bcaca]/10 text-[#2bcaca] rounded-sm text-xs"
                      size="sm"
                      variant="outline"
                    >
                      +{test.domains.length - 3}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Action */}
              <Button className="w-full rounded-sm mt-auto" size="sm" variant="outline">
                <Play className="mr-2 h-4 w-4" />
                Zobacz szczegóły
              </Button>
            </div>
          ))}
        </div>

        {filteredTests.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-gray-500">
              Nie znaleziono testów spełniających kryteria wyszukiwania.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default function NeurophysicsTestsPage() {
  return (
    <DashboardLayout>
      <NeurophysicsTests />
    </DashboardLayout>
  )
}
